import { formatClockTime } from '@/shared/lib/time-format';
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import type { AppointmentSlot, AppointmentValueSets, SlotResponse } from '@/types/scheduling-api';

/**
 * RecurrenceReselectTimeView — PROD-1011 "See all" sub-view.
 *
 * Re-enters slot selection for a SINGLE recurring occurrence, for the SAME
 * provider (Flutter `select_date_time_widget.dart` →
 * `TimeDateSelectionWidget(isRecurrence: true)`). Returns the picked slot via
 * `onPick`; never writes the main appointment form (recurrence is isolated —
 * see CONVERSION_NOTES PROD-1011 / Flutter's own `internalBloc`).
 *
 * Shares the wire call with the create flow (`useAvailableSlots`) — the picked
 * `SlotResponse` carries only id/start/end, so we stamp the provider/practice
 * context onto the returned `AppointmentSlot` (that's what books the occurrence).
 */

interface RecurrenceReselectTimeViewProps {
  providerId: string;
  providerName?: string;
  practiceId: string;
  practiceName?: string | null;
  appointmentProfileId?: string;
  appointmentType?: AppointmentValueSets | null;
  /** Occurrence date (ISO) — seeds the day shown. */
  initialDate: string;
  onPick: (slot: AppointmentSlot) => void;
  onCancel: () => void;
}

function toDateOnly(iso: string): Date {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d;
}

function fmtDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatClockTime(d);
}

/** Morning < 12:00, Afternoon < 17:00, else Evening. */
function bucketOf(iso?: string): 'morning' | 'afternoon' | 'evening' {
  const h = iso ? new Date(iso).getHours() : 0;
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export function RecurrenceReselectTimeView({
  providerId,
  providerName,
  practiceId,
  practiceName,
  appointmentProfileId,
  appointmentType,
  initialDate,
  onPick,
  onCancel,
}: RecurrenceReselectTimeViewProps) {
  const [date, setDate] = useState<Date>(() => toDateOnly(initialDate));

  const { data: slots = [], isLoading } = useAvailableSlots({
    practiceId,
    practitionerId: providerId,
    appointmentProfileId,
    start: date.toISOString(),
    end: date.toISOString(),
    count: 100,
  });

  const groups = useMemo(() => {
    const g: Record<'morning' | 'afternoon' | 'evening', SlotResponse[]> = {
      morning: [],
      afternoon: [],
      evening: [],
    };
    for (const s of slots) g[bucketOf(s.start)].push(s);
    return g;
  }, [slots]);

  const shiftDay = (delta: number) => {
    setDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const pick = (s: SlotResponse) => {
    // Stamp the provider/practice context — the bare SlotResponse only has
    // id/start/end, but the occurrence books on `selectedSlot.providerId`.
    onPick({
      start: s.start,
      end: s.end,
      providerId,
      practiceId,
      practiceName: practiceName ?? undefined,
      appointmentType: appointmentType ?? undefined,
      overbooked: false,
      comment: s.comment,
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="text-base font-bold text-text-primary">
          {providerName ? `Times — ${providerName}` : 'Select a time'}
        </h2>
        <span className="w-10" aria-hidden />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide px-4 py-4 space-y-4">
        {/* Day stepper */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => shiftDay(-1)}
            aria-label="Previous day"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default text-brand-primary hover:bg-blue-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-text-primary">{fmtDayLabel(date)}</span>
          <button
            type="button"
            onClick={() => shiftDay(1)}
            aria-label="Next day"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default text-brand-primary hover:bg-blue-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 h-24 text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading slots…</span>
          </div>
        ) : slots.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-8">
            No available slots on this day.
          </p>
        ) : (
          (['morning', 'afternoon', 'evening'] as const).map((bucket) =>
            groups[bucket].length > 0 ? (
              <div key={bucket}>
                <p className="text-xs font-medium text-text-muted mb-2 capitalize">
                  {bucket}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {groups[bucket].map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => pick(s)}
                      className={cn(
                        'h-10 rounded-md text-sm font-medium border border-border-default',
                        'bg-surface-card text-text-primary hover:border-brand-primary hover:bg-blue-100 hover:text-brand-primary transition-colors',
                      )}
                    >
                      {fmtTime(s.start)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null,
          )
        )}
      </div>
    </div>
  );
}
